import type { ComponentType } from 'react';

import helpArticleSources from 'virtual:help-article-sources';

import PublicApiAuthenticationEn from './content/en/public-api/authentication.mdx';
import PublicApiMetaTenantEn from './content/en/public-api/meta-tenant.mdx';
import PublicApiMetaScopesEn from './content/en/public-api/meta-scopes.mdx';
import PublicApiCampaignsListEn from './content/en/public-api/campaigns-list.mdx';
import PublicApiCampaignDetailEn from './content/en/public-api/campaign-detail.mdx';
import PublicApiCallsListEn from './content/en/public-api/calls-list.mdx';
import PublicApiCallsCreateEn from './content/en/public-api/calls-create.mdx';
import PublicApiCallDetailEn from './content/en/public-api/call-detail.mdx';
import PublicApiAgentsCatalogEn from './content/en/public-api/agents-catalog.mdx';
import PublicApiScriptsCatalogEn from './content/en/public-api/scripts-catalog.mdx';
import PublicApiUsersCatalogEn from './content/en/public-api/users-catalog.mdx';
import PublicApiTransferGroupsCatalogEn from './content/en/public-api/transfer-groups-catalog.mdx';

import PublicApiAuthenticationPtBr from './content/pt-BR/public-api/authentication.mdx';
import PublicApiMetaTenantPtBr from './content/pt-BR/public-api/meta-tenant.mdx';
import PublicApiMetaScopesPtBr from './content/pt-BR/public-api/meta-scopes.mdx';
import PublicApiCampaignsListPtBr from './content/pt-BR/public-api/campaigns-list.mdx';
import PublicApiCampaignDetailPtBr from './content/pt-BR/public-api/campaign-detail.mdx';
import PublicApiCallsListPtBr from './content/pt-BR/public-api/calls-list.mdx';
import PublicApiCallsCreatePtBr from './content/pt-BR/public-api/calls-create.mdx';
import PublicApiCallDetailPtBr from './content/pt-BR/public-api/call-detail.mdx';
import PublicApiAgentsCatalogPtBr from './content/pt-BR/public-api/agents-catalog.mdx';
import PublicApiScriptsCatalogPtBr from './content/pt-BR/public-api/scripts-catalog.mdx';
import PublicApiUsersCatalogPtBr from './content/pt-BR/public-api/users-catalog.mdx';
import PublicApiTransferGroupsCatalogPtBr from './content/pt-BR/public-api/transfer-groups-catalog.mdx';

import PublicApiAuthenticationEs from './content/es/public-api/authentication.mdx';
import PublicApiMetaTenantEs from './content/es/public-api/meta-tenant.mdx';
import PublicApiMetaScopesEs from './content/es/public-api/meta-scopes.mdx';
import PublicApiCampaignsListEs from './content/es/public-api/campaigns-list.mdx';
import PublicApiCampaignDetailEs from './content/es/public-api/campaign-detail.mdx';
import PublicApiCallsListEs from './content/es/public-api/calls-list.mdx';
import PublicApiCallsCreateEs from './content/es/public-api/calls-create.mdx';
import PublicApiCallDetailEs from './content/es/public-api/call-detail.mdx';
import PublicApiAgentsCatalogEs from './content/es/public-api/agents-catalog.mdx';
import PublicApiScriptsCatalogEs from './content/es/public-api/scripts-catalog.mdx';
import PublicApiUsersCatalogEs from './content/es/public-api/users-catalog.mdx';
import PublicApiTransferGroupsCatalogEs from './content/es/public-api/transfer-groups-catalog.mdx';

export type HelpLocaleCode = 'en' | 'pt-BR' | 'es';
export type HelpArticleKind = 'guide' | 'endpoint';

export interface HelpArticleLocaleEntry {
  Component: ComponentType<Record<string, unknown>>;
  source: string;
}

export interface HelpArticleDefinition {
  slug: string;
  sectionSlug: string;
  titleKey: string;
  descriptionKey: string;
  navGroupKey: string;
  navOrder: number;
  navLabelKey?: string;
  navLabel?: string;
  kind: HelpArticleKind;
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  endpointPath?: string;
  locales: Partial<Record<HelpLocaleCode, HelpArticleLocaleEntry>>;
}

export interface HelpSectionDefinition {
  slug: string;
  titleKey: string;
  descriptionKey: string;
  defaultArticleSlug: string;
}

/**
 * Returns the raw MDX text that backs a rendered help article.
 *
 * The copy-page action must use the source registry instead of importing
 * `*.mdx?source`, because the MDX compiler can still transform those imports
 * into React components in production builds.
 */
export function getHelpArticleSource(locale: HelpLocaleCode, fileName: string): string {
  return helpArticleSources[`${locale}/public-api/${fileName}`] ?? '';
}

export const helpSections: HelpSectionDefinition[] = [
  {
    slug: 'public-api',
    titleKey: 'pages:help.sections.publicApi.title',
    descriptionKey: 'pages:help.sections.publicApi.description',
    defaultArticleSlug: 'public-api/authentication',
  },
];

export const helpArticles: HelpArticleDefinition[] = [
  {
    slug: 'public-api/authentication',
    sectionSlug: 'public-api',
    titleKey: 'pages:help.sections.publicApi.articles.authentication.title',
    descriptionKey: 'pages:help.sections.publicApi.articles.authentication.description',
    navGroupKey: 'pages:help.sections.publicApi.navGroups.authentication',
    navOrder: 10,
    navLabelKey: 'pages:help.sections.publicApi.articles.authentication.navLabel',
    kind: 'guide',
    locales: {
      en: { Component: PublicApiAuthenticationEn, source: getHelpArticleSource('en', 'authentication.mdx') },
      'pt-BR': {
        Component: PublicApiAuthenticationPtBr,
        source: getHelpArticleSource('pt-BR', 'authentication.mdx'),
      },
      es: { Component: PublicApiAuthenticationEs, source: getHelpArticleSource('es', 'authentication.mdx') },
    },
  },
  {
    slug: 'public-api/metadata/organization',
    sectionSlug: 'public-api',
    titleKey: 'pages:help.sections.publicApi.articles.organizationMetadata.title',
    descriptionKey: 'pages:help.sections.publicApi.articles.organizationMetadata.description',
    navGroupKey: 'pages:help.sections.publicApi.navGroups.metadata',
    navOrder: 20,
    navLabelKey: 'pages:help.sections.publicApi.articles.organizationMetadata.navLabel',
    kind: 'endpoint',
    method: 'GET',
    endpointPath: '/public/v1/meta/tenant/',
    locales: {
      en: { Component: PublicApiMetaTenantEn, source: getHelpArticleSource('en', 'meta-tenant.mdx') },
      'pt-BR': { Component: PublicApiMetaTenantPtBr, source: getHelpArticleSource('pt-BR', 'meta-tenant.mdx') },
      es: { Component: PublicApiMetaTenantEs, source: getHelpArticleSource('es', 'meta-tenant.mdx') },
    },
  },
  {
    slug: 'public-api/metadata/scopes',
    sectionSlug: 'public-api',
    titleKey: 'pages:help.sections.publicApi.articles.scopesMetadata.title',
    descriptionKey: 'pages:help.sections.publicApi.articles.scopesMetadata.description',
    navGroupKey: 'pages:help.sections.publicApi.navGroups.metadata',
    navOrder: 30,
    navLabelKey: 'pages:help.sections.publicApi.articles.scopesMetadata.navLabel',
    kind: 'endpoint',
    method: 'GET',
    endpointPath: '/public/v1/meta/scopes/',
    locales: {
      en: { Component: PublicApiMetaScopesEn, source: getHelpArticleSource('en', 'meta-scopes.mdx') },
      'pt-BR': { Component: PublicApiMetaScopesPtBr, source: getHelpArticleSource('pt-BR', 'meta-scopes.mdx') },
      es: { Component: PublicApiMetaScopesEs, source: getHelpArticleSource('es', 'meta-scopes.mdx') },
    },
  },
  {
    slug: 'public-api/campaigns/list',
    sectionSlug: 'public-api',
    titleKey: 'pages:help.sections.publicApi.articles.campaignsList.title',
    descriptionKey: 'pages:help.sections.publicApi.articles.campaignsList.description',
    navGroupKey: 'pages:help.sections.publicApi.navGroups.campaigns',
    navOrder: 40,
    navLabelKey: 'pages:help.sections.publicApi.articles.campaignsList.navLabel',
    kind: 'endpoint',
    method: 'GET',
    endpointPath: '/public/v1/campaigns/',
    locales: {
      en: { Component: PublicApiCampaignsListEn, source: getHelpArticleSource('en', 'campaigns-list.mdx') },
      'pt-BR': {
        Component: PublicApiCampaignsListPtBr,
        source: getHelpArticleSource('pt-BR', 'campaigns-list.mdx'),
      },
      es: { Component: PublicApiCampaignsListEs, source: getHelpArticleSource('es', 'campaigns-list.mdx') },
    },
  },
  {
    slug: 'public-api/campaigns/detail',
    sectionSlug: 'public-api',
    titleKey: 'pages:help.sections.publicApi.articles.campaignDetail.title',
    descriptionKey: 'pages:help.sections.publicApi.articles.campaignDetail.description',
    navGroupKey: 'pages:help.sections.publicApi.navGroups.campaigns',
    navOrder: 50,
    navLabelKey: 'pages:help.sections.publicApi.articles.campaignDetail.navLabel',
    kind: 'endpoint',
    method: 'GET',
    endpointPath: '/public/v1/campaigns/{id}/',
    locales: {
      en: { Component: PublicApiCampaignDetailEn, source: getHelpArticleSource('en', 'campaign-detail.mdx') },
      'pt-BR': {
        Component: PublicApiCampaignDetailPtBr,
        source: getHelpArticleSource('pt-BR', 'campaign-detail.mdx'),
      },
      es: { Component: PublicApiCampaignDetailEs, source: getHelpArticleSource('es', 'campaign-detail.mdx') },
    },
  },
  {
    slug: 'public-api/calls/list',
    sectionSlug: 'public-api',
    titleKey: 'pages:help.sections.publicApi.articles.callsList.title',
    descriptionKey: 'pages:help.sections.publicApi.articles.callsList.description',
    navGroupKey: 'pages:help.sections.publicApi.navGroups.calls',
    navOrder: 60,
    navLabelKey: 'pages:help.sections.publicApi.articles.callsList.navLabel',
    kind: 'endpoint',
    method: 'GET',
    endpointPath: '/public/v1/calls/',
    locales: {
      en: { Component: PublicApiCallsListEn, source: getHelpArticleSource('en', 'calls-list.mdx') },
      'pt-BR': {
        Component: PublicApiCallsListPtBr,
        source: getHelpArticleSource('pt-BR', 'calls-list.mdx'),
      },
      es: { Component: PublicApiCallsListEs, source: getHelpArticleSource('es', 'calls-list.mdx') },
    },
  },
  {
    slug: 'public-api/calls/create',
    sectionSlug: 'public-api',
    titleKey: 'pages:help.sections.publicApi.articles.callsCreate.title',
    descriptionKey: 'pages:help.sections.publicApi.articles.callsCreate.description',
    navGroupKey: 'pages:help.sections.publicApi.navGroups.calls',
    navOrder: 65,
    navLabelKey: 'pages:help.sections.publicApi.articles.callsCreate.navLabel',
    kind: 'endpoint',
    method: 'POST',
    endpointPath: '/public/v1/calls/',
    locales: {
      en: { Component: PublicApiCallsCreateEn, source: getHelpArticleSource('en', 'calls-create.mdx') },
      'pt-BR': {
        Component: PublicApiCallsCreatePtBr,
        source: getHelpArticleSource('pt-BR', 'calls-create.mdx'),
      },
      es: { Component: PublicApiCallsCreateEs, source: getHelpArticleSource('es', 'calls-create.mdx') },
    },
  },
  {
    slug: 'public-api/calls/detail',
    sectionSlug: 'public-api',
    titleKey: 'pages:help.sections.publicApi.articles.callDetail.title',
    descriptionKey: 'pages:help.sections.publicApi.articles.callDetail.description',
    navGroupKey: 'pages:help.sections.publicApi.navGroups.calls',
    navOrder: 70,
    navLabelKey: 'pages:help.sections.publicApi.articles.callDetail.navLabel',
    kind: 'endpoint',
    method: 'GET',
    endpointPath: '/public/v1/calls/{id}/',
    locales: {
      en: { Component: PublicApiCallDetailEn, source: getHelpArticleSource('en', 'call-detail.mdx') },
      'pt-BR': {
        Component: PublicApiCallDetailPtBr,
        source: getHelpArticleSource('pt-BR', 'call-detail.mdx'),
      },
      es: { Component: PublicApiCallDetailEs, source: getHelpArticleSource('es', 'call-detail.mdx') },
    },
  },
  {
    slug: 'public-api/catalogs/agents',
    sectionSlug: 'public-api',
    titleKey: 'pages:help.sections.publicApi.articles.agentsCatalog.title',
    descriptionKey: 'pages:help.sections.publicApi.articles.agentsCatalog.description',
    navGroupKey: 'pages:help.sections.publicApi.navGroups.catalogs',
    navOrder: 80,
    navLabelKey: 'pages:help.sections.publicApi.articles.agentsCatalog.navLabel',
    kind: 'endpoint',
    method: 'GET',
    endpointPath: '/public/v1/agents/',
    locales: {
      en: { Component: PublicApiAgentsCatalogEn, source: getHelpArticleSource('en', 'agents-catalog.mdx') },
      'pt-BR': {
        Component: PublicApiAgentsCatalogPtBr,
        source: getHelpArticleSource('pt-BR', 'agents-catalog.mdx'),
      },
      es: { Component: PublicApiAgentsCatalogEs, source: getHelpArticleSource('es', 'agents-catalog.mdx') },
    },
  },
  {
    slug: 'public-api/catalogs/scripts',
    sectionSlug: 'public-api',
    titleKey: 'pages:help.sections.publicApi.articles.scriptsCatalog.title',
    descriptionKey: 'pages:help.sections.publicApi.articles.scriptsCatalog.description',
    navGroupKey: 'pages:help.sections.publicApi.navGroups.catalogs',
    navOrder: 90,
    navLabelKey: 'pages:help.sections.publicApi.articles.scriptsCatalog.navLabel',
    kind: 'endpoint',
    method: 'GET',
    endpointPath: '/public/v1/scripts/',
    locales: {
      en: { Component: PublicApiScriptsCatalogEn, source: getHelpArticleSource('en', 'scripts-catalog.mdx') },
      'pt-BR': {
        Component: PublicApiScriptsCatalogPtBr,
        source: getHelpArticleSource('pt-BR', 'scripts-catalog.mdx'),
      },
      es: { Component: PublicApiScriptsCatalogEs, source: getHelpArticleSource('es', 'scripts-catalog.mdx') },
    },
  },
  {
    slug: 'public-api/catalogs/users',
    sectionSlug: 'public-api',
    titleKey: 'pages:help.sections.publicApi.articles.usersCatalog.title',
    descriptionKey: 'pages:help.sections.publicApi.articles.usersCatalog.description',
    navGroupKey: 'pages:help.sections.publicApi.navGroups.catalogs',
    navOrder: 100,
    navLabelKey: 'pages:help.sections.publicApi.articles.usersCatalog.navLabel',
    kind: 'endpoint',
    method: 'GET',
    endpointPath: '/public/v1/users/',
    locales: {
      en: { Component: PublicApiUsersCatalogEn, source: getHelpArticleSource('en', 'users-catalog.mdx') },
      'pt-BR': {
        Component: PublicApiUsersCatalogPtBr,
        source: getHelpArticleSource('pt-BR', 'users-catalog.mdx'),
      },
      es: { Component: PublicApiUsersCatalogEs, source: getHelpArticleSource('es', 'users-catalog.mdx') },
    },
  },
  {
    slug: 'public-api/catalogs/transfer-groups',
    sectionSlug: 'public-api',
    titleKey: 'pages:help.sections.publicApi.articles.transferGroupsCatalog.title',
    descriptionKey: 'pages:help.sections.publicApi.articles.transferGroupsCatalog.description',
    navGroupKey: 'pages:help.sections.publicApi.navGroups.catalogs',
    navOrder: 110,
    navLabelKey: 'pages:help.sections.publicApi.articles.transferGroupsCatalog.navLabel',
    kind: 'endpoint',
    method: 'GET',
    endpointPath: '/public/v1/transfer-groups/',
    locales: {
      en: {
        Component: PublicApiTransferGroupsCatalogEn,
        source: getHelpArticleSource('en', 'transfer-groups-catalog.mdx'),
      },
      'pt-BR': {
        Component: PublicApiTransferGroupsCatalogPtBr,
        source: getHelpArticleSource('pt-BR', 'transfer-groups-catalog.mdx'),
      },
      es: {
        Component: PublicApiTransferGroupsCatalogEs,
        source: getHelpArticleSource('es', 'transfer-groups-catalog.mdx'),
      },
    },
  },
];

export function findHelpSection(slug?: string): HelpSectionDefinition | undefined {
  return helpSections.find((section) => section.slug === slug);
}

export function findHelpArticle(slug?: string): HelpArticleDefinition | undefined {
  return helpArticles.find((article) => article.slug === slug);
}

export function getHelpSectionArticles(sectionSlug: string): HelpArticleDefinition[] {
  return helpArticles
    .filter((article) => article.sectionSlug === sectionSlug)
    .sort((left, right) => left.navOrder - right.navOrder);
}

export function resolveHelpArticleLocale(
  article: HelpArticleDefinition,
  language: string,
): { locale: HelpLocaleCode; entry: HelpArticleLocaleEntry; fallbackUsed: boolean } {
  const exactLocale = article.locales[language as HelpLocaleCode];
  if (exactLocale) {
    return {
      locale: language as HelpLocaleCode,
      entry: exactLocale,
      fallbackUsed: false,
    };
  }
  return {
    locale: 'en',
    entry: article.locales.en as HelpArticleLocaleEntry,
    fallbackUsed: true,
  };
}

export function slugifyHelpHeading(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
